import { apiAdmin, apiService } from './apiService';
import { downloadJsonFile, readJsonFile, todayExportSuffix } from '../utils/masterDataIO';

export type MasterDataResource =
  | 'search_parameters'
  | 'report_templates'
  | 'ai_providers'
  | 'ai_summary_configs'
  | 'subscription_plans'
  | 'apis';
interface MasterDataEndpointConfig {
  exportPath: string;
  importPath: string;
  useAdminRequest: boolean;
}

const MASTER_DATA_ENDPOINTS: Record<MasterDataResource, MasterDataEndpointConfig> = {
  search_parameters: {
    exportPath: '/search-parameters/export-data/',
    importPath: '/search-parameters/import-data/',
    useAdminRequest: false,
  },
  report_templates: {
    exportPath: '/admin/report-templates/export-data/',
    importPath: '/admin/report-templates/import-data/',
    useAdminRequest: true,
  },
  ai_providers: {
    exportPath: '/ai-services/providers/export-data/',
    importPath: '/ai-services/providers/import-data/',
    useAdminRequest: false,
  },
  ai_summary_configs: {
    exportPath: '/admin/ai-summary-configs/export-data/',
    importPath: '/admin/ai-summary-configs/import-data/',
    useAdminRequest: true,
  },
  subscription_plans: {
    exportPath: '/subscriptions/admin/plans/export-data/',
    importPath: '/subscriptions/admin/plans/import-data/',
    useAdminRequest: false,
  },
  apis: {
    exportPath: '/admin/api-transformation/export/',
    importPath: '/admin/api-transformation/import_apis/',
    useAdminRequest: true,
  },
};

export const masterDataService = {
  async export(resource: MasterDataResource): Promise<unknown> {
    const config = MASTER_DATA_ENDPOINTS[resource];

    if (resource === 'apis') {
      const data = await apiAdmin.exportAPIs();
      downloadJsonFile(
        {
          resource_type: 'apis',
          version: 1,
          exported_at: new Date().toISOString(),
          record_count: Array.isArray(data) ? data.length : 0,
          records: data,
        },
        `apis_export_${todayExportSuffix()}.json`
      );
      return data;
    }

    const data = config.useAdminRequest
      ? await apiService.adminRequest<unknown>(config.exportPath, { method: 'GET' })
      : await apiService.request<unknown>(config.exportPath, { method: 'GET' });

    downloadJsonFile(data, `${resource}_export_${todayExportSuffix()}.json`);
    return data;
  },

  async import(resource: MasterDataResource, file: File): Promise<unknown> {
    const config = MASTER_DATA_ENDPOINTS[resource];
    const payload = await readJsonFile(file);

    if (resource === 'apis') {
      return apiService.adminRequest(config.importPath, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    }

    return config.useAdminRequest
      ? apiService.adminRequest(config.importPath, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      : apiService.request(config.importPath, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
  },
};
